import type { Theme } from '@goodtime/engine';
import { withHevcCopies } from './slotAssets';
// Veo-generated transparent celebration videos — see forestTheme.ts
// for the full pipeline notes. Same shape applied per-symbol here.
/**
 * Stormy Seas — the underwater-biome themed slot. Mapping:
 * cherry→bubble, lemon→starfish, orange→coral, bell→crab,
 * star(wild)→current_wild, seven→dolphin, gem→anchor,
 * scatter→pearl_scatter, money→treasure_coin.
 */
export const UNDERWATER_THEME: Theme = {
  id: 'underwater',
  name: 'Stormy Seas',
  palette: {
    accent: 0x42e2f5,         // tropical cyan
    secondary: 0x1f6b9b,      // deep ocean blue
    warm: 0xffd23b,           // gold-treasure yellow
    frameFill: 0x0a1828,      // abyss
    bgGradientTop: 0x051022,
    bgGradientBottom: 0x1a4a78,
    dimOverlay: 0x010810,
  },
  slot: {
    symbols: [
      { id: 'cherry',  label: 'Bubble',    glyph: '🫧', tint: 0x8be0ff, weight: 5,   textureAlias: 'underwater:cherry',
        winVideoAlias: 'underwater:cherry:win:video', winVideoSpeed: 2 },
      { id: 'lemon',   label: 'Starfish',  glyph: '⭐', tint: 0xffb85c, weight: 5,   textureAlias: 'underwater:lemon',
        winVideoAlias: 'underwater:lemon:win:video', winVideoSpeed: 2 },
      { id: 'orange',  label: 'Coral',     glyph: '🪸', tint: 0xff6b9d, weight: 4,   textureAlias: 'underwater:orange',
        winVideoAlias: 'underwater:orange:win:video', winVideoSpeed: 2 },
      { id: 'bell',    label: 'Crab',      glyph: '🦀', tint: 0xff5252, weight: 3,   textureAlias: 'underwater:bell',
        winVideoAlias: 'underwater:bell:win:video', winVideoSpeed: 2 },
      { id: 'star',    label: 'Current',   glyph: '🌊', tint: 0x42e2f5, weight: 2,   textureAlias: 'underwater:star', // wild
        winVideoAlias: 'underwater:star:win:video', winVideoSpeed: 2 },
      { id: 'seven',   label: 'Dolphin',   glyph: '🐬', tint: 0x8bbed1, weight: 1,   textureAlias: 'underwater:seven',
        winVideoAlias: 'underwater:seven:win:video', winVideoSpeed: 2 },
      { id: 'gem',     label: 'Anchor',    glyph: '⚓', tint: 0x9aa6b5, weight: 1,   textureAlias: 'underwater:gem',
        winVideoAlias: 'underwater:gem:win:video', winVideoSpeed: 2 },
      { id: 'scatter', label: 'Pearl',     glyph: '🦪', tint: 0xfff59d, weight: 1,   textureAlias: 'underwater:scatter',
        winVideoAlias: 'underwater:scatter:win:video', winVideoSpeed: 2 },
      { id: 'money',   label: 'Treasure',  glyph: '$', tint: 0xc8a01f, weight: 1.5,
        textureAlias: 'underwater:money',
        winVideoAlias: 'underwater:money:win:video', winVideoSpeed: 2,
        isMoney: true },
      // Gem-on-reel — producer brief 2026-06-04. Scatter-style bonus
      // shared across every biome (registered via SHARED_REEL_SYMBOLS
      // in themes/index.ts). Weights deliberately rare so a gem
      // landing feels like a treat rather than background noise.
      { id: 'gem_t5',  label: 'Gems',     glyph: '💎', tint: 0x3ea7e8, weight: 0.10, gemValue: 5,  textureAlias: 'shared:reel:gem_5' },
      { id: 'gem_t10', label: 'Gem Pile', glyph: '💎', tint: 0x3ea7e8, weight: 0.05, gemValue: 10, textureAlias: 'shared:reel:gem_10' },
    ],
    wildSymbolId: 'star',
    scatterSymbolId: 'scatter',
  },
  wheel: {
    segments: [
      { id: 'x1-a',   kind: 'multiplier', label: '×1',  tint: 0x42e2f5, multiplier: 1,  weight: 4 },
      { id: 'x2-a',   kind: 'multiplier', label: '×2',  tint: 0x1f6b9b, multiplier: 2,  weight: 3 },
      { id: 'x0-a',   kind: 'multiplier', label: '✕',   tint: 0x0a1828, multiplier: 0,  weight: 2 },
      { id: 'x3-a',   kind: 'multiplier', label: '×3',  tint: 0xff6b9d, multiplier: 3,  weight: 2 },
      { id: 'x1-b',   kind: 'multiplier', label: '×1',  tint: 0x42e2f5, multiplier: 1,  weight: 3 },
      { id: 'x5-a',   kind: 'multiplier', label: '×5',  tint: 0xffd23b, multiplier: 5,  weight: 1 },
      { id: 'x0-b',   kind: 'multiplier', label: '✕',   tint: 0x0a1828, multiplier: 0,  weight: 2 },
      { id: 'x10-a',  kind: 'multiplier', label: '×10', tint: 0xfff59d, multiplier: 10, weight: 1 },
      { id: 'x2-b',   kind: 'multiplier', label: '×2',  tint: 0x1f6b9b, multiplier: 2,  weight: 3 },
      { id: 'x1-c',   kind: 'multiplier', label: '×1',  tint: 0x42e2f5, multiplier: 1,  weight: 3 },
      { id: 'x3-b',   kind: 'multiplier', label: '×3',  tint: 0xff6b9d, multiplier: 3,  weight: 2 },
      { id: 'jackpot',kind: 'multiplier', label: '×25', tint: 0xc8a01f, multiplier: 25, weight: 1 },
    ],
  },
  assets: {
    slot: {
      cherry:  { alias: 'underwater:cherry',  path: 'reel/underwater/bubble.png' },
      lemon:   { alias: 'underwater:lemon',   path: 'reel/underwater/starfish.png' },
      orange:  { alias: 'underwater:orange',  path: 'reel/underwater/coral.png' },
      bell:    { alias: 'underwater:bell',    path: 'reel/underwater/crab.png' },
      star:    { alias: 'underwater:star',    path: 'reel/underwater/current_wild.png' },
      seven:   { alias: 'underwater:seven',   path: 'reel/underwater/dolphin.png' },
      gem:     { alias: 'underwater:gem',     path: 'reel/underwater/anchor.png' },
      scatter: { alias: 'underwater:scatter', path: 'reel/underwater/pearl_scatter.png' },
      money:   { alias: 'underwater:money',   path: 'reel/underwater/treasure_coin.png' },
    },
    slotWin: withHevcCopies({
      cherry:  { alias: 'underwater:cherry:win:video',  path: 'reel/underwater/bubble_win.webm' },
      lemon:   { alias: 'underwater:lemon:win:video',   path: 'reel/underwater/starfish_win.webm' },
      orange:  { alias: 'underwater:orange:win:video',  path: 'reel/underwater/coral_win.webm' },
      bell:    { alias: 'underwater:bell:win:video',    path: 'reel/underwater/crab_win.webm' },
      star:    { alias: 'underwater:star:win:video',    path: 'reel/underwater/current_wild_win.webm' },
      seven:   { alias: 'underwater:seven:win:video',   path: 'reel/underwater/dolphin_win.webm' },
      gem:     { alias: 'underwater:gem:win:video',     path: 'reel/underwater/anchor_win.webm' },
      scatter: { alias: 'underwater:scatter:win:video', path: 'reel/underwater/pearl_scatter_win.webm' },
      money:   { alias: 'underwater:money:win:video',   path: 'reel/underwater/treasure_coin_win.webm' },
    }),
    scene: {
      background:       { alias: 'underwater:bg',        path: 'environment/underwater.png' },
      columnBackground: { alias: 'underwater:column_bg', path: 'reel/underwater_column_bg.png' },
      border:           { alias: 'underwater:border',    path: 'brand/border_underwater.png' },
    },
  },
};
